import React, { useContext } from 'react';
import { Box, Typography, Stack, CircularProgress } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import PaymentIcon from '@mui/icons-material/Payment';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HomeIcon from '@mui/icons-material/Home';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import ReplayIcon from '@mui/icons-material/Replay';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { OrderHistory, OrderHistoryAction } from '../../types';
import { palette, typography } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';

interface OrderHistoryTimelineProps {
  history: OrderHistory[] | undefined;
  isLoading?: boolean;
}

interface ActionConfig {
  label: string;
  icon: React.ReactElement;
  color: string;
}

const getActionConfig = (action: OrderHistoryAction): ActionConfig => {
  switch (action) {
    case OrderHistoryAction.ORDER_CREATED:
      return {
        label: 'Commande Cr\u00e9\u00e9e',
        icon: <AddCircleOutlineIcon sx={{ fontSize: 16 }} />,
        color: palette.primary,
      };
    case OrderHistoryAction.ORDER_UPDATED:
      return {
        label: 'Commande Mise \u00e0 Jour',
        icon: <EditIcon sx={{ fontSize: 16 }} />,
        color: palette.info,
      };
    case OrderHistoryAction.PAYMENT_RECEIVED:
      return {
        label: 'Paiement Re\u00e7u',
        icon: <PaymentIcon sx={{ fontSize: 16 }} />,
        color: palette.success,
      };
    case OrderHistoryAction.PAYMENT_PARTIALLY_RECEIVED:
      return {
        label: 'Paiement Partiel Re\u00e7u',
        icon: <PaymentIcon sx={{ fontSize: 16 }} />,
        color: palette.warning,
      };
    case OrderHistoryAction.PAYMENT_STATUS_CHANGED:
      return {
        label: 'Statut Paiement Modifi\u00e9',
        icon: <PaymentIcon sx={{ fontSize: 16 }} />,
        color: palette.info,
      };
    case OrderHistoryAction.FULFILLMENT_STARTED:
      return {
        label: 'Traitement D\u00e9marr\u00e9',
        icon: <LocalShippingIcon sx={{ fontSize: 16 }} />,
        color: palette.info,
      };
    case OrderHistoryAction.FULFILLMENT_STATUS_CHANGED:
      return {
        label: 'Statut Traitement Modifi\u00e9',
        icon: <LocalShippingIcon sx={{ fontSize: 16 }} />,
        color: palette.info,
      };
    case OrderHistoryAction.ITEM_TRANSFERRED:
      return {
        label: 'Article Transf\u00e9r\u00e9',
        icon: <SwapHorizIcon sx={{ fontSize: 16 }} />,
        color: palette.accent,
      };
    case OrderHistoryAction.TRANSFER_ISSUE:
      return {
        label: 'Probl\u00e8me de Transfert',
        icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
        color: palette.error,
      };
    case OrderHistoryAction.READY_FOR_PICKUP:
      return {
        label: 'Pr\u00eat \u00e0 Retirer',
        icon: <StorefrontIcon sx={{ fontSize: 16 }} />,
        color: palette.accent,
      };
    case OrderHistoryAction.READY_FOR_DELIVERY:
      return {
        label: 'Pr\u00eat \u00e0 Livrer',
        icon: <LocalShippingIcon sx={{ fontSize: 16 }} />,
        color: palette.accent,
      };
    case OrderHistoryAction.OUT_FOR_DELIVERY:
      return {
        label: 'En Livraison',
        icon: <DirectionsCarIcon sx={{ fontSize: 16 }} />,
        color: palette.info,
      };
    case OrderHistoryAction.DELIVERED:
      return {
        label: 'Livr\u00e9',
        icon: <HomeIcon sx={{ fontSize: 16 }} />,
        color: palette.success,
      };
    case OrderHistoryAction.PICKED_UP:
      return {
        label: 'Retir\u00e9',
        icon: <ShoppingBagIcon sx={{ fontSize: 16 }} />,
        color: palette.success,
      };
    case OrderHistoryAction.ORDER_COMPLETED:
      return {
        label: 'Commande Termin\u00e9e',
        icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
        color: palette.success,
      };
    case OrderHistoryAction.ORDER_CANCELLED:
      return {
        label: 'Commande Annul\u00e9e',
        icon: <CancelIcon sx={{ fontSize: 16 }} />,
        color: palette.error,
      };
    case OrderHistoryAction.ORDER_EXPIRED:
      return {
        label: 'Commande Expir\u00e9e',
        icon: <TimerOffIcon sx={{ fontSize: 16 }} />,
        color: palette.textMuted,
      };
    case OrderHistoryAction.REFUND_ISSUED:
      return {
        label: 'Remboursement \u00c9mis',
        icon: <ReplayIcon sx={{ fontSize: 16 }} />,
        color: palette.warning,
      };
    case OrderHistoryAction.REFUND_PARTIAL:
      return {
        label: 'Remboursement Partiel',
        icon: <MoneyOffIcon sx={{ fontSize: 16 }} />,
        color: palette.warning,
      };
    case OrderHistoryAction.REFUND_FULL:
      return {
        label: 'Remboursement Complet',
        icon: <MoneyOffIcon sx={{ fontSize: 16 }} />,
        color: palette.textMuted,
      };
    case OrderHistoryAction.DELIVERY_ADDRESS_UPDATED:
      return {
        label: 'Adresse de Livraison Mise \u00e0 Jour',
        icon: <LocationOnIcon sx={{ fontSize: 16 }} />,
        color: palette.info,
      };
    case OrderHistoryAction.B2B_APPROVAL_PENDING:
      return {
        label: 'Approbation B2B en Attente',
        icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
        color: palette.warning,
      };
    case OrderHistoryAction.B2B_APPROVAL_APPROVED:
      return {
        label: 'Approbation B2B Accept\u00e9e',
        icon: <ThumbUpIcon sx={{ fontSize: 16 }} />,
        color: palette.success,
      };
    case OrderHistoryAction.B2B_APPROVAL_REJECTED:
      return {
        label: 'Approbation B2B Refus\u00e9e',
        icon: <ThumbDownIcon sx={{ fontSize: 16 }} />,
        color: palette.error,
      };
    default:
      return {
        label: action,
        icon: <HistoryIcon sx={{ fontSize: 16 }} />,
        color: palette.textMuted,
      };
  }
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString('fr-DZ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const OrderHistoryTimeline: React.FC<OrderHistoryTimelineProps> = ({
  history,
  isLoading = false,
}) => {
  const { mode } = useContext(ColorModeContext);

  // Theme-aware colors
  const colors = mode === 'dark' ? {
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
    border: palette.border,
  } : {
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
    border: palette.light.border,
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <HistoryIcon sx={{ fontSize: 40, color: colors.textMuted, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Aucun historique disponible
        </Typography>
      </Box>
    );
  }

  // Sort history by date, most recent first
  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Stack spacing={0}>
      {sortedHistory.map((entry, index) => {
        const actionConfig = getActionConfig(entry.action);
        const isLast = index === sortedHistory.length - 1;

        return (
          <Box key={entry.id} display="flex" gap={2}>
            {/* Timeline line and dot */}
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: `${actionConfig.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: actionConfig.color,
                  flexShrink: 0,
                }}
              >
                {actionConfig.icon}
              </Box>
              {!isLast && (
                <Box
                  sx={{
                    width: 2,
                    flex: 1,
                    minHeight: 40,
                    bgcolor: colors.border,
                  }}
                />
              )}
            </Box>

            {/* Content */}
            <Box pb={isLast ? 0 : 3} flex={1} minWidth={0}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  fontFamily: typography.fontFamily.body,
                  color: colors.textPrimary,
                }}
              >
                {actionConfig.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: colors.textSecondary }}
              >
                {formatDate(entry.createdAt)}
              </Typography>
              {entry.performedBy && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: colors.textSecondary }}
                >
                  par {entry.performedBy.firstName} {entry.performedBy.lastName}
                </Typography>
              )}
              {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                <Box mt={0.5}>
                  <>
                    {entry.metadata.previousStatus && entry.metadata.newStatus && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: colors.textMuted }}
                      >
                        {String(entry.metadata.previousStatus)} → {String(entry.metadata.newStatus)}
                      </Typography>
                    )}
                    {entry.metadata.amount && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: colors.textMuted }}
                      >
                        Montant: {new Intl.NumberFormat('fr-DZ', {
                          style: 'currency',
                          currency: 'DZD',
                        }).format(Number(entry.metadata.amount))}
                      </Typography>
                    )}
                    {entry.metadata.reason && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: colors.textMuted }}
                      >
                        Raison: {String(entry.metadata.reason)}
                      </Typography>
                    )}
                  </>
                </Box>
              )}
              {entry.notes && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: colors.textMuted, mt: 0.5 }}
                >
                  {entry.notes}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default OrderHistoryTimeline;
